import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StableInput, StableTextarea } from "@/components/StableField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  addInstallment, createCategory, createExpense, createPayment, createVendor,
  deleteInstallment, getExpense, listCategories, listInstallments,
  listOccurrences, listPayments, listPaymentMethods, listVendors, patchOccurrence,
  updateExpense, updateInstallment,
} from "@/lib/api";
import {
  EUR, fmtDate, isoLocalDate, paymentMethodIT, paymentMethodOptions,
  recurrenceIT, statusIT,
} from "@/lib/format";
import { SearchableCombobox } from "@/components/SearchableCombobox";
import { Trash2 } from "lucide-react";

const RECURRENCES = ["one_time", "monthly", "bi_monthly", "quarterly", "every_4_months", "semi_annual", "annual", "custom_plan"];

/* Preview occurrences client-side so user sees impact before saving.
   Reproduces backend recurrence.py stepping logic for the first 12 occurrences
   (or fewer if an end_date stops the series earlier). */
const previewOccurrences = (
  startDate,
  recurrenceType,
  dayOfMonth,
  endDate,
  count = 12,
) => {
  if (!startDate || !recurrenceType) return [];
  const limit = endDate ? new Date(endDate) : null;
  const dates = [];
  const anchor = dayOfMonth || new Date(startDate).getDate();
  let cur = new Date(startDate);
  cur = clampToDay(cur.getFullYear(), cur.getMonth(), anchor);
  if (limit && cur > limit) return dates;
  dates.push(cur);
  if (recurrenceType === "one_time") return dates;

  const step = {
    monthly: 1,
    bi_monthly: 2,
    quarterly: 3,
    every_4_months: 4,
    semi_annual: 6,
    annual: 12,
  }[recurrenceType];
  for (let i = 1; i < count; i++) {
    const prev = dates[i - 1];
    const next = new Date(prev.getFullYear(), prev.getMonth() + step, 1);
    const candidate = clampToDay(next.getFullYear(), next.getMonth(), anchor);
    if (limit && candidate > limit) break;
    dates.push(candidate);
  }
  return dates;
};

const clampToDay = (year, month, day) => {
  const last = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, last));
};

export default function ExpenseForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sourceExpenseId = searchParams.get("from_expense");

  /* Mode discriminator: routes under /ordinary drive the single-occurrence
     "Spesa ordinaria" UX. The same component is reused so all the shared
     concerns (categories, vendors, validation, duplica) stay in one place. */
  const isOrdinary = location.pathname.startsWith("/ordinary");
  const listPath = isOrdinary ? "/ordinary" : "/expenses";

  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [existing, setExisting] = useState(null);
  const [affected, setAffected] = useState(null);
  // For ordinary edit mode: keep the single occurrence handy so we can show
  // status, paid amount, last method, and inline-register a payment.
  const [ordinaryOcc, setOrdinaryOcc] = useState(null);
  const [lastPayment, setLastPayment] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category_id: "",
    vendor_id: "",
    planned_amount: "",
    currency: "EUR",
    recurrence_type: isOrdinary ? "one_time" : "monthly",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    day_of_month: "",
    notes: "",
    default_payment_method_id: "",
  });

  /* Per-installment customizations (creation flow only, recurring mode).
     Keyed by sequence index 0..N-1. Each entry can contain {amount, title}.
     Values are kept as strings to mirror the input fields directly. */
  const [customRows, setCustomRows] = useState({});

  /* Ordinary creation flow (NEW): a spesa ordinaria nasce di default come
     "prima nota" — già pagata alla data indicata, niente scadenza aperta.
     L'utente può optare per "Crea come scadenza da pagare" per il vecchio
     comportamento. Solo applicabile in creazione (non in edit). */
  const [payOnCreation, setPayOnCreation] = useState(true);
  const [creationPayMethod, setCreationPayMethod] = useState("bank_transfer");

  /* Custom installment plan rows ("Piano rateale", recurrence_type=custom_plan).
     - Creation mode: in-memory list, submitted with the master in a single POST.
       Each row: {due_date, planned_amount, description}. No id yet.
     - Edit mode: same shape, plus {id, has_payments, total_paid, status, sequence_no}.
       Mutations go through the dedicated installments endpoints immediately. */
  const [planRows, setPlanRows] = useState([]);
  const isCustomPlan = !isOrdinary && form.recurrence_type === "custom_plan";
  const planTotal = useMemo(
    () => planRows.reduce((s, r) => s + (Number(r.planned_amount) || 0), 0),
    [planRows],
  );

  /* Total-distribution helper input. Kept separately because it's a UI-only
     control that fans out into customRows when the user clicks 'Distribuisci'. */
  const [distributeTotal, setDistributeTotal] = useState("");

  /* Inline payment dialog state (ordinary edit mode only). */
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [paying, setPaying] = useState(false);

  const loadOrdinaryOccurrence = async (expenseId) => {
    try {
      const occs = await listOccurrences({ expense_id: expenseId, limit: 1 });
      const occ = (occs || [])[0] || null;
      setOrdinaryOcc(occ);
      if (occ) {
        const pays = await listPayments({ occurrence_id: occ.id });
        const sorted = [...(pays || [])].sort((a, b) =>
          (b.paid_at || "").localeCompare(a.paid_at || ""),
        );
        setLastPayment(sorted[0] || null);
      }
    } catch {
      // Non-fatal: just hide the payment panel.
      setOrdinaryOcc(null);
    }
  };

  useEffect(() => {
    (async () => {
      const [c, v, pm] = await Promise.all([
        listCategories(),
        listVendors(),
        listPaymentMethods().catch(() => []),
      ]);
      setCategories(c || []);
      setVendors(v || []);
      setPaymentMethods(pm || []);
      if (editing) {
        const e = await getExpense(id);
        setExisting(e);
        setForm({
          title: e.title || "",
          description: e.description || "",
          category_id: e.category_id || "",
          vendor_id: e.vendor_id || "",
          planned_amount: String(e.planned_amount ?? ""),
          currency: e.currency || "EUR",
          recurrence_type: e.recurrence_type || "monthly",
          start_date: e.start_date || "",
          end_date: e.end_date || "",
          day_of_month: e.day_of_month || "",
          notes: e.notes || "",
          default_payment_method_id: e.default_payment_method_id || "",
        });
        if (isOrdinary) {
          await loadOrdinaryOccurrence(id);
        } else if (e.recurrence_type === "custom_plan") {
          // Load existing installments for the plan.
          try {
            const items = await listInstallments(id);
            setPlanRows(
              (items || []).map((r) => ({
                id: r.id,
                sequence_no: r.sequence_no,
                due_date: r.due_date,
                planned_amount: String(r.planned_amount ?? ""),
                description: r.description || "",
                has_payments: !!r.has_payments,
                total_paid: r.total_paid,
                status: r.status,
              })),
            );
          } catch {
            toast.error("Errore caricamento rate del piano");
          }
        } else {
          // affected occurrences — future, unmodified, planned (recurring only)
          const today = new Date().toISOString().slice(0, 10);
          const occs = await listOccurrences({
            expense_id: id,
            date_from: today,
            limit: 500,
          });
          setAffected(occs || []);
        }
      } else if (sourceExpenseId) {
        // Duplica: prefill from another series, NEW record on submit.
        try {
          const src = await getExpense(sourceExpenseId);
          setForm({
            title: src.title ? `${src.title} (copia)` : "",
            description: src.description || "",
            category_id: src.category_id || "",
            vendor_id: src.vendor_id || "",
            planned_amount: String(src.planned_amount ?? ""),
            currency: src.currency || "EUR",
            recurrence_type: isOrdinary ? "one_time" : src.recurrence_type || "monthly",
            start_date: new Date().toISOString().slice(0, 10),
            end_date: "",
            day_of_month: src.day_of_month || "",
            notes: src.notes || "",
            default_payment_method_id: src.default_payment_method_id || "",
          });
          toast.success(
            isOrdinary
              ? "Dati duplicati: rivedi e salva per creare una nuova spesa ordinaria."
              : "Dati duplicati: rivedi e salva per creare una nuova serie.",
          );
        } catch {
          toast.error("Impossibile duplicare la voce selezionata.");
        }
      }
    })();
  }, [id, editing, sourceExpenseId, isOrdinary]);

  const preview = useMemo(
    () =>
      isOrdinary
        ? []
        : previewOccurrences(
            form.start_date,
            form.recurrence_type,
            form.day_of_month ? Number(form.day_of_month) : null,
            form.end_date || null,
            12,
          ),
    [isOrdinary, form.start_date, form.recurrence_type, form.day_of_month, form.end_date],
  );

  /* Per-row effective amount (override or master), expressed in numeric form
     for the running total / diff calculation. */
  const effectiveRowAmount = (i) => {
    const row = customRows[i];
    if (row && row.amount !== undefined && row.amount !== "") {
      return Number(row.amount) || 0;
    }
    return Number(form.planned_amount || 0);
  };

  const runningTotal = useMemo(() => {
    let s = 0;
    for (let i = 0; i < preview.length; i++) s += effectiveRowAmount(i);
    return s;
  }, [preview.length, customRows, form.planned_amount]);

  const runningDiff = useMemo(() => {
    if (!distributeTotal) return 0;
    return runningTotal - Number(distributeTotal);
  }, [runningTotal, distributeTotal]);

  /* Distribute the typed total equally across all preview installments.
     Uses integer cents to avoid floating-point drift; the LAST installment
     absorbs any rounding remainder so the sum exactly matches the entered
     total. After distribution every cell is a customRow override, so the
     post-create PATCH sweep saves them as is_modified=true. */
  const applyDistribution = () => {
    const total = Number(distributeTotal);
    const n = preview.length;
    if (!total || total <= 0 || n === 0) return;
    const cents = Math.round(total * 100);
    const baseCents = Math.floor(cents / n);
    const lastCents = cents - baseCents * (n - 1);
    const next = { ...customRows };
    for (let i = 0; i < n; i++) {
      const value = (i === n - 1 ? lastCents : baseCents) / 100;
      next[i] = { ...next[i], amount: value.toFixed(2) };
    }
    setCustomRows(next);
    toast.success(
      n === 1
        ? "Importo applicato all'unica rata."
        : `Importo distribuito su ${n} rate (l'ultima assorbe il resto).`,
    );
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.title.trim()) return "Titolo obbligatorio";
    if (!form.category_id) return "Categoria obbligatoria";
    if (isCustomPlan) {
      if (!editing && planRows.length === 0)
        return "Aggiungi almeno una rata al piano";
      for (let i = 0; i < planRows.length; i++) {
        const r = planRows[i];
        if (!r.due_date) return `Rata ${i + 1}: data obbligatoria`;
        const amt = Number(r.planned_amount);
        if (!r.planned_amount || Number.isNaN(amt) || amt < 0)
          return `Rata ${i + 1}: importo non valido`;
      }
    } else {
      if (!form.planned_amount || Number(form.planned_amount) < 0)
        return "Importo obbligatorio e non negativo";
    }
    if (!form.start_date)
      return isOrdinary ? "Data scadenza obbligatoria" : "Data di decorrenza obbligatoria";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const payload = {
      title: form.title,
      description: form.description || null,
      category_id: form.category_id,
      vendor_id: form.vendor_id || null,
      planned_amount: isCustomPlan ? String(planTotal) : String(form.planned_amount),
      currency: form.currency,
      recurrence_type: isOrdinary ? "one_time" : form.recurrence_type,
      start_date: form.start_date,
      end_date: isOrdinary || isCustomPlan ? null : form.end_date || null,
      day_of_month: isOrdinary || isCustomPlan
        ? null
        : form.day_of_month
          ? Number(form.day_of_month)
          : null,
      notes: form.notes || null,
      is_ordinary: isOrdinary,
      // Metodo di pagamento predefinito solo su ricorrenti (non su ordinarie)
      default_payment_method_id: isOrdinary
        ? null
        : form.default_payment_method_id || null,
    };
    if (!editing && isCustomPlan) {
      payload.installments = planRows.map((r) => ({
        due_date: r.due_date,
        planned_amount: String(r.planned_amount),
        description: r.description || null,
      }));
    }
    if (!editing && isOrdinary) {
      // Prima-nota flow: by default register the spend as already paid on
      // start_date. The user can opt-out to keep the legacy "scadenza
      // aperta da pagare" workflow.
      payload.pay_on_creation = payOnCreation;
      if (payOnCreation) {
        payload.payment_method = creationPayMethod;
      }
    }
    if (editing && isCustomPlan) {
      // Master.planned_amount is auto-recomputed by the installments router
      // on every rata mutation. Don't overwrite it from the form.
      delete payload.planned_amount;
    }
    try {
      if (editing) {
        await updateExpense(id, payload);
        toast.success(
          isOrdinary
            ? "Spesa ordinaria aggiornata."
            : "Serie aggiornata: scadenze future non modificate ricalcolate",
        );
      } else {
        const created = await createExpense(payload);
        if (isOrdinary) {
          toast.success(
            payOnCreation
              ? "Spesa ordinaria registrata come pagata."
              : "Spesa ordinaria creata come scadenza da pagare.",
          );
        } else if (isCustomPlan) {
          toast.success(
            `Piano rateale creato · ${planRows.length} rate generate.`,
          );
        } else {
          // Apply per-installment customizations (amount / title) to the
          // newly-generated occurrences. Entries with empty values are skipped.
          const dirty = Object.entries(customRows).filter(([, val]) => {
            const base = String(form.planned_amount);
            const amountChanged =
              val?.amount !== undefined &&
              val.amount !== "" &&
              String(val.amount) !== base;
            const titleChanged =
              val?.title !== undefined && val.title !== "" && val.title !== form.title;
            return amountChanged || titleChanged;
          });
          if (dirty.length) {
            const occs = await listOccurrences({
              expense_id: created.id,
              limit: 500,
            });
            const bySeq = new Map(occs.map((o) => [o.sequence_no, o]));
            let patched = 0;
            let failed = 0;
            for (const [idxStr, override] of dirty) {
              const idx = Number(idxStr);
              // Backend assigns sequence_no starting at 1.
              const occ = bySeq.get(idx + 1);
              if (!occ) continue;
              const body = {};
              if (
                override.amount !== undefined &&
                override.amount !== "" &&
                String(override.amount) !== String(form.planned_amount)
              ) {
                body.planned_amount = String(override.amount);
              }
              if (
                override.title !== undefined &&
                override.title !== "" &&
                override.title !== form.title
              ) {
                body.title_override = override.title;
              }
              if (Object.keys(body).length) {
                try {
                  await patchOccurrence(occ.id, body);
                  patched += 1;
                } catch {
                  failed += 1;
                }
              }
            }
            if (failed > 0) {
              toast.error(
                `Scadenza creata, ma ${failed} personalizzazion${failed === 1 ? "e non è stata applicata" : "i non sono state applicate"} (${patched} ok).`,
              );
            } else {
              toast.success(
                `Scadenza ricorrente creata · ${patched} rate personalizzate`,
              );
            }
          } else {
            toast.success("Scadenza ricorrente creata, scadenze generate");
          }
        }
      }
      navigate(listPath);
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio"));
    }
  };

  /* ---- Inline payment for ordinary edit mode ------------------------- */
  const openPayDialog = () => {
    if (!ordinaryOcc) return;
    const remaining =
      Number(ordinaryOcc.effective_amount) - Number(ordinaryOcc.total_paid || 0);
    setPayAmount(remaining > 0 ? remaining.toFixed(2) : ordinaryOcc.effective_amount);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayMethod(lastPayment?.method || "bank_transfer");
    setPayRef("");
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!ordinaryOcc || paying) return;
    if (!payAmount || Number(payAmount) <= 0) {
      toast.error("Importo non valido");
      return;
    }
    setPaying(true);
    try {
      await createPayment({
        occurrence_id: ordinaryOcc.id,
        paid_amount: String(payAmount),
        paid_at: payDate,
        method: payMethod,
        reference: payRef || null,
      });
      toast.success("Pagamento registrato.");
      setPayOpen(false);
      await loadOrdinaryOccurrence(id);
    } catch (e) {
      toast.error(formatApiError(e, "Errore registrazione pagamento"));
    } finally {
      setPaying(false);
    }
  };

  const futureUnmodified = (affected || []).filter(
    (o) => !o.is_modified && o.status === "planned",
  );
  const preservedCount = (affected || []).length - futureUnmodified.length;

  /* -------- UI -------- */
  const headerTitle = isOrdinary
    ? editing
      ? "Modifica spesa ordinaria"
      : "Nuova spesa ordinaria"
    : editing
      ? "Modifica serie ricorrente"
      : "Nuova scadenza ricorrente";

  const headerSubtitle = isOrdinary
    ? "Spesa pianificabile con singola scadenza. Non genera serie ricorrenti, ma compare in Scadenze, Cruscotto, Previsione e Report."
    : editing
      ? "Le modifiche qui riguardano l'intera serie — le scadenze modificate singolarmente restano intatte."
      : "Definisci una scadenza ricorrente. Le scadenze pianificate vengono generate automaticamente.";

  const occStatus = ordinaryOcc?.status || "planned";
  const todayIso = new Date().toISOString().slice(0, 10);
  const isOverdue =
    ordinaryOcc &&
    (occStatus === "planned" || occStatus === "partially_paid") &&
    ordinaryOcc.due_date < todayIso;
  const remaining = ordinaryOcc
    ? Number(ordinaryOcc.effective_amount) - Number(ordinaryOcc.total_paid || 0)
    : 0;

  return (
    <div data-testid={isOrdinary ? "ordinary-form-page" : "expense-form-page"}>
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        actions={
          <Button asChild variant="outline" data-testid="btn-back">
            <Link to={listPath}>← Torna all&apos;elenco</Link>
          </Button>
        }
      />

      <div
        className={
          isOrdinary || isCustomPlan
            ? "grid grid-cols-1 gap-6"
            : "grid grid-cols-1 gap-6 lg:grid-cols-3"
        }
      >
        <div
          className={
            "rounded-xl border border-stone-200 bg-white p-6 " +
            (isOrdinary || isCustomPlan ? "" : "lg:col-span-2")
          }
        >
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
            {isOrdinary
              ? "Dati spesa ordinaria"
              : isCustomPlan
                ? "Dati piano rateale"
                : "Dati serie ricorrente"}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>{isOrdinary ? "Descrizione" : "Titolo"}</Label>
              <StableInput
                data-testid="input-title"
                value={form.title}
                onCommit={(v) => setField("title", v)}
              />
            </div>
            <div>
              <Label>Categoria</Label>
              <SearchableCombobox
                value={form.category_id}
                onChange={(v) => setField("category_id", v)}
                onSelectComplete={() => {
                  requestAnimationFrame(() => {
                    document
                      .querySelector('[data-testid="input-vendor-trigger"]')
                      ?.focus();
                  });
                }}
                options={categories}
                onCreate={async (name) => {
                  try {
                    const created = await createCategory({ name });
                    setCategories((prev) => [...prev, created]);
                    toast.success(`Categoria "${name}" creata`);
                    return created;
                  } catch {
                    toast.error("Errore creazione categoria");
                    return null;
                  }
                }}
                placeholder="Seleziona categoria"
                createLabel="Crea nuova categoria:"
                emptyLabel="Nessuna categoria trovata"
                testId="input-category"
              />
            </div>
            <div>
              <Label>Fornitore (opzionale)</Label>
              <SearchableCombobox
                value={form.vendor_id}
                onChange={(v) => setField("vendor_id", v)}
                onSelectComplete={() => {
                  // Vendor è opzionale: dopo selezione porta il focus al
                  // primo campo importo (occurrence rate o expense amount).
                  requestAnimationFrame(() => {
                    document
                      .querySelector('[data-testid="input-amount"]')
                      ?.focus();
                  });
                }}
                options={vendors}
                onCreate={async (name) => {
                  try {
                    const created = await createVendor({ name });
                    setVendors((prev) => [...prev, created]);
                    toast.success(`Fornitore "${name}" creato`);
                    return created;
                  } catch {
                    toast.error("Errore creazione fornitore");
                    return null;
                  }
                }}
                placeholder="Seleziona fornitore"
                createLabel="Crea nuovo fornitore:"
                emptyLabel="Nessun fornitore trovato"
                testId="input-vendor"
              />
            </div>
            {!isOrdinary && (
              <div>
                <Label>Metodo di pagamento (opzionale)</Label>
                <Select
                  value={form.default_payment_method_id || "__none__"}
                  onValueChange={(v) =>
                    setField(
                      "default_payment_method_id",
                      v === "__none__" ? "" : v,
                    )
                  }
                >
                  <SelectTrigger data-testid="input-payment-method">
                    <SelectValue placeholder="Non specificato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Non specificato —</SelectItem>
                    {paymentMethods
                      .filter((m) => m.is_active)
                      .map((m) => (
                        <SelectItem
                          key={m.id}
                          value={m.id}
                          data-testid={`input-payment-method-${m.id}`}
                        >
                          {m.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[10px] text-stone-400">
                  Es. SDD, Bonifico, Contanti/Cassa. Aggiungi/modifica gli
                  elementi da Impostazioni → Metodi di pagamento.
                </p>
              </div>
            )}
            <div>
              <Label>{isOrdinary ? "Importo previsto (EUR)" : isCustomPlan ? "Totale piano (EUR, auto)" : "Importo pianificato (EUR)"}</Label>
              <Input
                type="number"
                step="0.01"
                data-testid="input-amount"
                value={isCustomPlan ? planTotal.toFixed(2) : form.planned_amount}
                onChange={(e) => setField("planned_amount", e.target.value)}
                readOnly={isCustomPlan}
                disabled={isCustomPlan}
                className={isCustomPlan ? "bg-stone-50 text-stone-600" : ""}
              />
              {isCustomPlan && (
                <p className="mt-1 text-[11px] text-stone-500">
                  Somma automatica delle rate del piano ({planRows.length}{" "}
                  {planRows.length === 1 ? "rata" : "rate"}).
                </p>
              )}
            </div>
            <div>
              <Label>
                {isOrdinary
                  ? payOnCreation
                    ? "Data spesa (pagata)"
                    : "Data scadenza"
                  : "Decorrenza"}
              </Label>
              <Input
                type="date"
                data-testid="input-start-date"
                value={form.start_date}
                onChange={(e) => setField("start_date", e.target.value)}
              />
            </div>
            {isOrdinary && !editing && (
              <div
                data-testid="ordinary-flow-block"
                className="md:col-span-2 rounded-lg border border-stone-200 bg-stone-50/40 p-3"
              >
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-stone-600">
                  Stato iniziale della registrazione
                </div>
                <div
                  role="radiogroup"
                  aria-label="Stato iniziale della registrazione"
                  className="grid gap-2 md:grid-cols-2"
                >
                  <label
                    htmlFor="ordinary-status-paid"
                    data-testid="radio-status-paid-label"
                    className={
                      "flex cursor-pointer items-start gap-2 rounded-md border p-2 transition-colors " +
                      (payOnCreation
                        ? "border-stone-800 bg-white"
                        : "border-stone-200 bg-white/40 hover:bg-white")
                    }
                  >
                    <input
                      type="radio"
                      id="ordinary-status-paid"
                      name="ordinary-status"
                      data-testid="radio-status-paid"
                      checked={payOnCreation}
                      onChange={() => setPayOnCreation(true)}
                      className="mt-1 h-3.5 w-3.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-stone-800">
                        Già pagata
                      </div>
                      <p className="mt-0.5 text-[11px] leading-snug text-stone-500">
                        Spesa già sostenuta. Crea il pagamento e il movimento
                        in Prima Nota. Stato: <strong>Pagata</strong>.
                      </p>
                    </div>
                  </label>
                  <label
                    htmlFor="ordinary-status-todo"
                    data-testid="radio-status-todo-label"
                    className={
                      "flex cursor-pointer items-start gap-2 rounded-md border p-2 transition-colors " +
                      (!payOnCreation
                        ? "border-amber-500 bg-amber-50/60"
                        : "border-stone-200 bg-white/40 hover:bg-white")
                    }
                  >
                    <input
                      type="radio"
                      id="ordinary-status-todo"
                      name="ordinary-status"
                      data-testid="radio-status-todo"
                      checked={!payOnCreation}
                      onChange={() => setPayOnCreation(false)}
                      className="mt-1 h-3.5 w-3.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-stone-800">
                        Da pagare
                      </div>
                      <p className="mt-0.5 text-[11px] leading-snug text-stone-500">
                        Fattura ricevuta ma non ancora pagata. Crea solo
                        la scadenza in <strong>Scadenze Spese</strong>; nessun
                        movimento in Prima Nota fino al pagamento.
                      </p>
                    </div>
                  </label>
                </div>
                {payOnCreation && (
                  <div
                    data-testid="creation-pay-method-block"
                    className="mt-3 max-w-xs"
                  >
                    <Label className="text-[10px] uppercase tracking-wider text-stone-500">
                      Metodo di pagamento
                    </Label>
                    <Select
                      value={creationPayMethod}
                      onValueChange={setCreationPayMethod}
                    >
                      <SelectTrigger
                        data-testid="select-creation-pay-method"
                        className="h-8 text-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bonifico</SelectItem>
                        <SelectItem value="cash">Contanti</SelectItem>
                        <SelectItem value="card">Carta / POS</SelectItem>
                        <SelectItem value="direct_debit">Addebito SDD / RID</SelectItem>
                        <SelectItem value="other">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            {!isOrdinary && (
              <>
                <div>
                  <Label>Tipo ricorrenza</Label>
                  <Select
                    value={form.recurrence_type}
                    onValueChange={(v) => setField("recurrence_type", v)}
                  >
                    <SelectTrigger data-testid="input-recurrence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCES.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`recurrence-option-${r}`}>{recurrenceIT[r]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!isCustomPlan && (
                  <>
                    <div>
                      <Label>Fine (opzionale)</Label>
                      <Input
                        type="date"
                        data-testid="input-end-date"
                        value={form.end_date}
                        onChange={(e) => setField("end_date", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Giorno del mese (1-31, opzionale)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        data-testid="input-day-of-month"
                        value={form.day_of_month}
                        onChange={(e) => setField("day_of_month", e.target.value)}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div className="md:col-span-2">
              <Label>Note</Label>
              <StableTextarea
                data-testid="input-notes"
                value={form.notes}
                onCommit={(v) => setField("notes", v)}
              />
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2">
            <Button data-testid="btn-submit" onClick={submit}>
              {isOrdinary
                ? editing
                  ? "Salva modifiche"
                  : payOnCreation
                    ? "Registra spesa pagata"
                    : "Crea scadenza da pagare"
                : isCustomPlan
                  ? editing
                    ? "Salva modifiche piano"
                    : `Crea piano rateale (${planRows.length} ${planRows.length === 1 ? "rata" : "rate"})`
                  : editing
                    ? "Salva modifiche serie"
                    : "Crea scadenza ricorrente"}
            </Button>
            <Button asChild variant="outline">
              <Link to={listPath}>Annulla</Link>
            </Button>
          </div>

          {isCustomPlan && (
            <PlanInstallments
              editing={editing}
              expenseId={id}
              rows={planRows}
              setRows={setPlanRows}
            />
          )}

          {!isOrdinary && !isCustomPlan && editing && (
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
              <strong>Impatto sulle scadenze</strong>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Scadenze future non modificate e in programma:{" "}
                  <strong>{futureUnmodified.length}</strong> → verranno
                  aggiornate con i nuovi valori.
                </li>
                <li>
                  Scadenze preservate (modificate singolarmente, pagate,
                  parziali): <strong>{preservedCount}</strong> → rimangono come sono.
                </li>
              </ul>
            </div>
          )}

          {/* Inline payment / status panel for ordinary edit */}
          {isOrdinary && editing && ordinaryOcc && (
            <div
              data-testid="ordinary-payment-panel"
              className="mt-6 rounded-lg border border-stone-200 bg-stone-50/60 p-4"
            >
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="text-[11px] uppercase tracking-wider text-stone-500">
                  Stato pagamento
                </span>
                <span
                  data-testid="ordinary-status-badge"
                  className={
                    "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                    (isOverdue
                      ? "bg-rose-600 text-white"
                      : occStatus === "paid"
                        ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                        : occStatus === "partially_paid"
                          ? "border border-amber-200 bg-amber-50 text-amber-800"
                          : "border border-blue-200 bg-blue-50 text-blue-700")
                  }
                >
                  {isOverdue ? "Scaduta" : statusIT[occStatus] || occStatus}
                </span>
                <span className="text-xs text-stone-500">
                  Scadenza <strong className="font-mono tabular-nums">{fmtDate(ordinaryOcc.due_date)}</strong>
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Tile label="Importo" value={EUR(ordinaryOcc.effective_amount)} testId="tile-planned" />
                <Tile label="Pagato" value={EUR(ordinaryOcc.total_paid || 0)} testId="tile-paid" tone="emerald" />
                <Tile
                  label="Da pagare"
                  value={EUR(remaining)}
                  testId="tile-remaining"
                  tone={remaining > 0 ? "amber" : "stone"}
                />
                <Tile
                  label="Ultimo metodo"
                  value={lastPayment ? paymentMethodIT[lastPayment.method] || "—" : "—"}
                  testId="tile-method"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {occStatus !== "paid" && (
                  <Button
                    data-testid="btn-register-payment"
                    onClick={openPayDialog}
                  >
                    Registra pagamento
                  </Button>
                )}
                <Button asChild variant="outline" data-testid="btn-go-occurrence">
                  <Link to={`/occurrences?q=${encodeURIComponent(form.title || "")}`}>
                    Apri in Scadenze
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>

        {!isOrdinary && !isCustomPlan && (
          <aside className="rounded-xl border border-stone-200 bg-white p-6">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-stone-600">
              Anteprima scadenze
            </h2>
            <p className="mb-4 text-xs text-stone-500">
              {editing
                ? `Prime 12 scadenze in base alla configurazione corrente (${recurrenceIT[form.recurrence_type]})${form.end_date ? ", interrotte alla data di fine serie" : ""}.`
                : `Personalizza importo e descrizione di ogni rata prima del salvataggio (${recurrenceIT[form.recurrence_type]})${form.end_date ? ", serie interrotta alla data di fine" : ""}.`}
            </p>

            {!editing && preview.length > 0 && (
              <div
                data-testid="distribute-bar"
                className="mb-3 flex flex-col gap-3 rounded-lg border border-stone-200 bg-stone-50/60 p-3"
              >
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                      Distribuisci importo totale
                    </Label>
                    <p className="mt-0.5 text-[11px] text-stone-500">
                      Divide equamente l&apos;importo su tutte le {preview.length} rate;
                      l&apos;ultima assorbe il resto del centesimo.
                    </p>
                  </div>
                  <Input
                    data-testid="input-distribute-total"
                    type="number"
                    step="0.01"
                    placeholder="es. 12000"
                    value={distributeTotal}
                    onChange={(e) => setDistributeTotal(e.target.value)}
                    className="h-9 w-[140px] text-right font-mono tabular-nums"
                  />
                  <Button
                    type="button"
                    size="sm"
                    data-testid="btn-distribute-apply"
                    onClick={applyDistribution}
                    disabled={
                      !distributeTotal ||
                      Number(distributeTotal) <= 0 ||
                      preview.length === 0
                    }
                    className="h-9"
                  >
                    Distribuisci
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="btn-reset-customizations"
                    disabled={Object.keys(customRows).length === 0}
                    onClick={() => {
                      setCustomRows({});
                      setDistributeTotal("");
                    }}
                    className="h-9 text-[11px]"
                  >
                    Reset personalizzazioni
                  </Button>
                </div>
                <div className="flex items-baseline justify-between gap-3 border-t border-stone-200 pt-2 text-xs">
                  <span className="text-stone-500">Totale calcolato delle rate</span>
                  <span
                    data-testid="distribute-running-total"
                    className="font-mono text-sm font-semibold tabular-nums text-stone-900"
                  >
                    {EUR(runningTotal)}
                  </span>
                </div>
                {distributeTotal && Number(distributeTotal) > 0 && (
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="text-stone-500">
                      Differenza rispetto al totale inserito
                    </span>
                    <span
                      data-testid="distribute-diff"
                      className={
                        "font-mono tabular-nums " +
                        (Math.abs(runningDiff) < 0.005
                          ? "text-emerald-700"
                          : "text-amber-700")
                      }
                    >
                      {runningDiff > 0 ? "+" : ""}
                      {EUR(runningDiff)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <ul data-testid="preview-list" className="divide-y divide-stone-100">
              {preview.length === 0 && (
                <li className="py-3 text-center text-xs text-stone-500">
                  Nessuna scadenza generata: la data di fine precede la data di inizio.
                </li>
              )}
              {preview.map((d, i) => {
                const iso = isoLocalDate(d);
                if (editing) {
                  return (
                    <li
                      key={i}
                      className="flex items-center justify-between py-2 text-sm"
                    >
                      <span className="text-stone-600">#{i + 1}</span>
                      <span className="font-mono tabular-nums text-stone-900">
                        {fmtDate(iso)}
                      </span>
                      <span className="font-mono tabular-nums text-stone-500">
                        {EUR(form.planned_amount)}
                      </span>
                    </li>
                  );
                }
                const row = customRows[i] || {};
                const isCustom =
                  (row.amount !== undefined && row.amount !== "" &&
                    String(row.amount) !== String(form.planned_amount)) ||
                  (row.title !== undefined && row.title !== "" &&
                    row.title !== form.title);
                return (
                  <li
                    key={i}
                    data-testid={`preview-row-${i}`}
                    className="grid grid-cols-[28px_88px_minmax(0,1fr)_120px] items-center gap-2 py-2"
                  >
                    <span
                      className={
                        "text-[11px] font-bold tabular-nums " +
                        (isCustom ? "text-amber-700" : "text-stone-400")
                      }
                      title={isCustom ? "Rata personalizzata" : ""}
                    >
                      #{i + 1}
                    </span>
                    <span className="font-mono text-xs tabular-nums text-stone-700">
                      {fmtDate(iso)}
                    </span>
                    <Input
                      data-testid={`preview-title-${i}`}
                      placeholder={form.title || "Descrizione (opzionale)"}
                      value={row.title ?? ""}
                      onChange={(e) =>
                        setCustomRows((m) => ({
                          ...m,
                          [i]: { ...m[i], title: e.target.value },
                        }))
                      }
                      className="h-8 text-xs"
                    />
                    <Input
                      data-testid={`preview-amount-${i}`}
                      type="number"
                      step="0.01"
                      placeholder={String(form.planned_amount || "")}
                      value={row.amount ?? ""}
                      onChange={(e) =>
                        setCustomRows((m) => ({
                          ...m,
                          [i]: { ...m[i], amount: e.target.value },
                        }))
                      }
                      className="h-8 text-right font-mono text-xs tabular-nums"
                    />
                  </li>
                );
              })}
            </ul>

            {!editing && (
              <p className="mt-3 text-[11px] text-stone-500">
                Lascia vuoto per usare i valori della serie. Le rate personalizzate
                vengono contrassegnate come modificate e non vengono ricalcolate
                quando aggiorni la serie.
              </p>
            )}

            {existing && !existing.is_active && (
              <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">
                Serie attualmente interrotta dal {fmtDate(existing.stopped_at)}.
                Riattivala dall&apos;elenco spese.
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Inline payment dialog (ordinary edit only) */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="dialog-pay-ordinary">
          <DialogHeader>
            <DialogTitle>Registra pagamento</DialogTitle>
            <DialogDescription>
              Anche pagamenti parziali sono supportati. Lo stato viene ricalcolato automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Importo</Label>
              <StableInput
                type="number"
                step="0.01"
                data-testid="input-pay-amount"
                value={payAmount}
                onCommit={setPayAmount}
              />
              {ordinaryOcc && (
                <p className="mt-1 text-xs text-stone-500">
                  Pianificato {EUR(ordinaryOcc.effective_amount)} — già pagato {EUR(ordinaryOcc.total_paid || 0)}
                </p>
              )}
            </div>
            <div>
              <Label>Data pagamento (cassa)</Label>
              <Input
                type="date"
                data-testid="input-pay-date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Metodo di pagamento</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="select-pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethodOptions.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`select-pay-method-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Riferimento (es. CRO)</Label>
              <StableInput
                data-testid="input-pay-ref"
                value={payRef}
                onCommit={setPayRef}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annulla</Button>
            <Button data-testid="btn-confirm-pay" onClick={submitPay} disabled={paying}>
              {paying ? "Registrazione…" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, tone = "stone", testId }) {
  const colors = {
    stone: "text-stone-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  };
  return (
    <div data-testid={testId} className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className={"font-mono text-sm font-semibold tabular-nums " + (colors[tone] || colors.stone)}>
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Plan installments grid ("Piano rateale" / recurrence_type=custom_plan)
 * - Creation mode: in-memory rows; final POST happens with the master.
 * - Edit mode: each add/edit/delete hits the dedicated installments endpoint
 *   directly so the master.planned_amount stays auto-summed server-side.
 * Per requirement (option b): rate already paid (or partially) can only have
 * description edited; date/amount inputs are disabled; delete is blocked.
 * ------------------------------------------------------------------------- */
function PlanInstallments({ editing, expenseId, rows, setRows }) {
  const today = new Date().toISOString().slice(0, 10);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    due_date: today,
    planned_amount: "",
    description: "",
  });

  const addRow = async () => {
    if (!draft.due_date) {
      toast.error("Data rata obbligatoria");
      return;
    }
    const amt = Number(draft.planned_amount);
    if (!draft.planned_amount || Number.isNaN(amt) || amt < 0) {
      toast.error("Importo rata non valido");
      return;
    }
    if (editing && expenseId) {
      setAdding(true);
      try {
        const created = await addInstallment(expenseId, {
          due_date: draft.due_date,
          planned_amount: String(amt),
          description: draft.description || null,
        });
        setRows((prev) => [
          ...prev,
          {
            id: created.id,
            sequence_no: created.sequence_no,
            due_date: created.due_date,
            planned_amount: String(created.planned_amount),
            description: created.description || "",
            has_payments: !!created.has_payments,
            total_paid: created.total_paid,
            status: created.status,
          },
        ]);
        toast.success("Rata aggiunta.");
      } catch (e) {
        toast.error(formatApiError(e, "Errore aggiunta rata"));
        setAdding(false);
        return;
      } finally {
        setAdding(false);
      }
    } else {
      setRows((prev) => [
        ...prev,
        {
          due_date: draft.due_date,
          planned_amount: String(amt),
          description: draft.description || "",
        },
      ]);
    }
    setDraft({ due_date: draft.due_date, planned_amount: "", description: "" });
  };

  const updateField = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const persistField = async (idx, field) => {
    if (!editing || !expenseId) return;
    const row = rows[idx];
    if (!row?.id) return;
    const original = row.__original?.[field];
    const value = row[field];
    if (original !== undefined && original === value) return;
    const payload = {};
    if (field === "due_date") payload.due_date = value;
    if (field === "planned_amount") payload.planned_amount = String(value || "0");
    if (field === "description") payload.description = value || null;
    try {
      const updated = await updateInstallment(expenseId, row.id, payload);
      setRows((prev) =>
        prev.map((r, i) =>
          i === idx
            ? {
                ...r,
                due_date: updated.due_date,
                planned_amount: String(updated.planned_amount),
                description: updated.description || "",
                has_payments: !!updated.has_payments,
                __original: undefined,
              }
            : r,
        ),
      );
    } catch (e) {
      toast.error(formatApiError(e, "Errore aggiornamento rata"));
      // Roll back to last known value if available
      if (original !== undefined) updateField(idx, field, original);
    }
  };

  const snapshotOriginal = (idx, field) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const orig = r.__original || {};
        if (orig[field] !== undefined) return r;
        return { ...r, __original: { ...orig, [field]: r[field] } };
      }),
    );
  };

  const removeRow = async (idx) => {
    const row = rows[idx];
    if (!window.confirm(`Eliminare la rata #${idx + 1}?`)) return;
    if (editing && expenseId && row?.id) {
      try {
        await deleteInstallment(expenseId, row.id);
      } catch (e) {
        toast.error(formatApiError(e, "Errore eliminazione rata"));
        return;
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
    toast.success("Rata eliminata.");
  };

  const total = rows.reduce((s, r) => s + (Number(r.planned_amount) || 0), 0);

  return (
    <div
      data-testid="plan-installments"
      className="mt-6 rounded-xl border border-stone-200 bg-stone-50/30 p-5"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-600">
          Rate del piano
        </h3>
        <span className="text-xs text-stone-500">
          {rows.length} {rows.length === 1 ? "rata" : "rate"} · totale{" "}
          <strong className="font-mono tabular-nums text-stone-900">
            {EUR(total)}
          </strong>
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Data scadenza</th>
              <th className="px-3 py-2 text-right">Importo</th>
              <th className="px-3 py-2 text-left">Descrizione</th>
              <th className="px-3 py-2 text-left">Stato</th>
              <th className="w-[60px]" />
            </tr>
          </thead>
          <tbody>
            {/* New row */}
            <tr data-testid="plan-add-row" className="border-t border-stone-200 bg-emerald-50/30">
              <td className="px-3 py-2 text-xs text-stone-400">+</td>
              <td className="px-2 py-1">
                <Input
                  data-testid="plan-add-date"
                  type="date"
                  value={draft.due_date}
                  onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                  className="h-8 text-xs"
                />
              </td>
              <td className="px-2 py-1">
                <Input
                  data-testid="plan-add-amount"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={draft.planned_amount}
                  onChange={(e) => setDraft({ ...draft, planned_amount: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addRow();
                  }}
                  className="h-8 text-right font-mono text-xs tabular-nums"
                />
              </td>
              <td className="px-2 py-1">
                <Input
                  data-testid="plan-add-description"
                  type="text"
                  placeholder="Es. Rottamazione Q1"
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="h-8 text-xs"
                />
              </td>
              <td />
              <td className="px-2 py-1">
                <Button
                  size="sm"
                  data-testid="plan-add-btn"
                  onClick={addRow}
                  disabled={adding}
                  className="h-8"
                >
                  Aggiungi
                </Button>
              </td>
            </tr>
            {/* Existing rows */}
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  data-testid="plan-empty"
                  className="px-3 py-6 text-center text-xs text-stone-400"
                >
                  Nessuna rata. Aggiungi la prima sopra.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => {
                const locked = !!r.has_payments;
                return (
                  <tr
                    key={r.id || `new-${idx}`}
                    data-testid={`plan-row-${idx}`}
                    className="border-t border-stone-100 hover:bg-stone-50/50"
                  >
                    <td className="px-3 py-1.5 text-xs text-stone-500">
                      {idx + 1}
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="date"
                        data-testid={`plan-row-date-${idx}`}
                        value={r.due_date || ""}
                        disabled={locked}
                        onFocus={() => snapshotOriginal(idx, "due_date")}
                        onChange={(e) => updateField(idx, "due_date", e.target.value)}
                        onBlur={() => persistField(idx, "due_date")}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        data-testid={`plan-row-amount-${idx}`}
                        value={r.planned_amount || ""}
                        disabled={locked}
                        onFocus={() => snapshotOriginal(idx, "planned_amount")}
                        onChange={(e) => updateField(idx, "planned_amount", e.target.value)}
                        onBlur={() => persistField(idx, "planned_amount")}
                        className="h-8 text-right font-mono text-xs tabular-nums"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="text"
                        data-testid={`plan-row-description-${idx}`}
                        value={r.description || ""}
                        onFocus={() => snapshotOriginal(idx, "description")}
                        onChange={(e) => updateField(idx, "description", e.target.value)}
                        onBlur={() => persistField(idx, "description")}
                        className="h-8 text-xs"
                        placeholder="(facoltativo)"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      {locked ? (
                        <span
                          data-testid={`plan-row-locked-${idx}`}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                          title={`Pagato ${EUR(r.total_paid || 0)}`}
                        >
                          Pagata
                        </span>
                      ) : (
                        <span className="text-stone-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`plan-row-delete-${idx}`}
                        onClick={() => removeRow(idx)}
                        disabled={locked}
                        className="h-7 w-7 text-stone-400 hover:text-rose-600 disabled:opacity-30"
                        title={
                          locked
                            ? "Rata con pagamenti — elimina prima i pagamenti"
                            : "Elimina rata"
                        }
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-stone-500">
        Ogni rata diventa una scadenza nel sistema (Cruscotto, Scadenze,
        Previsione, Report). Le rate con pagamenti collegati possono essere
        modificate solo nella descrizione: per cambiare data o importo,
        elimina prima i pagamenti.
      </p>
    </div>
  );
}
